import { Card } from "@/components/ui/Card";
import { cn } from "@/lib/cn";
import { Dumbbell, Search, Brain, Salad } from "lucide-react";

export interface EstimateCardProps {
  toolName: string;
  args: unknown;
}

function getToolIcon(toolName: string) {
  if (toolName.includes("log")) return Dumbbell;
  if (toolName.includes("search")) return Search;
  if (toolName.includes("estimate") && toolName.includes("Food")) return Salad;
  if (toolName.includes("estimate")) return Brain;
  return Brain;
}

function getToolLabel(toolName: string) {
  if (toolName.includes("logMeal")) return "Log meal";
  if (toolName.includes("logExercise")) return "Log exercise";
  if (toolName.includes("searchFoodMemory")) return "Search memory";
  if (toolName.includes("searchCalorieData")) return "Web search";
  if (toolName.includes("estimateFoodImage")) return "Analyze photo";
  if (toolName.includes("estimateFoodText")) return "Estimate food";
  if (toolName.includes("estimateExercise")) return "Estimate exercise";
  return toolName;
}

function extractArgValue(args: unknown, keys: string[]): string | undefined {
  if (typeof args !== "object" || args === null) return undefined;
  const record = args as Record<string, unknown>;
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.length > 0) return value;
    if (typeof value === "number") return String(value);
  }
  return undefined;
}

function extractKcal(args: unknown, keys: string[]): number | undefined {
  if (typeof args !== "object" || args === null) return undefined;
  const record = args as Record<string, unknown>;
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "number" && value >= 0) return value;
  }
  return undefined;
}

export function EstimateCard({ toolName, args }: EstimateCardProps) {
  const Icon = getToolIcon(toolName);
  const label = getToolLabel(toolName);
  const description =
    extractArgValue(args, ["description", "name", "type", "query"]) ??
    "Unknown item";
  const kcal = extractKcal(args, [
    "estimatedKcal",
    "estimatedBurnedKcal",
    "calories",
  ]);

  return (
    <Card
      className={cn(
        "max-w-[85%] border-l-4 border-l-accent p-3 shadow-none",
        "bg-surface",
      )}
      data-testid="estimate-card"
    >
      <div className="flex items-center gap-2">
        <div className="rounded-lg bg-accent-light p-1.5 text-accent">
          <Icon size={16} strokeWidth={2} />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold text-text">{label}</div>
          <div className="text-xs text-text-muted line-clamp-2">
            {description}
          </div>
          {kcal !== undefined && (
            <div className="mt-1 text-xs font-bold text-accent">
              {kcal} kcal
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
